import { supabase } from './supabase';
import { uploadGuestList } from './tables';
import type { Registry, RegistryItem, CreateRegistryDTO } from '../types/registry';

export async function createRegistry(data: CreateRegistryDTO) {
  const {
    title,
    description,
    event_date,
    currency,
    items,
    delivery_address,
    contact_number,
    account_holder_name,
    account_number,
    bank_name,
    bank_branch_name,
    thank_you_message,
    fee_responsibility,
    platform_fee_percentage,
    gateway_fee_percentage,
    paypal_email,
    guestList
  } = data;
  
  // Get current user
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  if (userError || !user) throw new Error('User must be authenticated');

  // Create registry
  const { data: registry, error: registryError } = await supabase
    .from('registries')
    .insert({
      title,
      description,
      event_date,
      currency,
      delivery_address,
      contact_number,
      account_holder_name,
      account_number,
      bank_name,
      bank_branch_name,
      thank_you_message,
      user_id: user.id,
      fee_responsibility,
      platform_fee_percentage,
      gateway_fee_percentage,
      paypal_email: paypal_email || null,
      is_public: true
    })
    .select()
    .single();

  if (registryError) throw registryError;
  if (!registry) throw new Error('Failed to create registry');

  // Insert items
  const registryItems = items.map(item => ({
    ...item,
    registry_id: registry.id
  }));

  const { error: itemsError } = await supabase
    .from('registry_items')
    .insert(registryItems);

  if (itemsError) throw itemsError;

  // Upload guest list if provided
  if (guestList && guestList.length > 0) {
    const uploadResult = await uploadGuestList(registry.id, guestList);
    if (!uploadResult.success) {
      console.error('Failed to upload guest list:', uploadResult.message);
      // Don't fail the registry creation if guest list upload fails
    }
  }

  return registry;
}

export async function getRegistries() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User must be authenticated');

  // Get registries owned by the user
  const { data: ownedRegistries, error: ownedError } = await supabase
    .from('registries')
    .select(`
      *,
      items:registry_items(*)
    `)
    .eq('user_id', user.id);

  if (ownedError) throw ownedError;

  return ownedRegistries as (Registry & { items: RegistryItem[] })[];
}

export async function getRegistry(id: string) {
  try {
    // First check if the registry exists and get its details
    const { data: registry, error: registryError } = await supabase
      .from('registries')
      .select(`
        *,
        items:registry_items(*),
        theme
      `)
      .eq('id', id)
      .single();

    if (registryError) {
      console.error('Registry fetch error:', registryError);
      throw new Error('Registry not found');
    }

    if (!registry) {
      throw new Error('Registry not found');
    }

    return registry as Registry & { items: RegistryItem[] };
  } catch (error) {
    console.error('Get registry error:', error);
    throw error;
  }
}